// Integrations — the area, and QuickBooks as its first tenant.
//
// Session 78n9.3, Issue #298 (and #424's visible half).
//
// WHY THIS IS NOT IN COMPANY SETTINGS, which is what gate decision D14 said:
// Colby's instruction at this session's open overturns D14 on both counts —
// the placement and the role set. His reasoning, and it is the right one: an
// accounting integration is not a setting. A setting is a value you pick once
// and forget. This is a live connection with state — connected or not, a token
// that expires, a company it points at, two caches that go stale, a mapping
// table and a push history. Company settings is already eleven blocks in one
// scroll and this would have been the twelfth.
//
// The competitive research agreed and is recorded in #298: QuickBooks Online
// itself separates its Apps hub from Account and settings; Xero has Connected
// apps as its own destination; Odoo's Xero integration needed three sub-menus
// the day it shipped. NetSuite is the counter-example — token auth buried at
// Setup > Company > Enable Features — and it is the one with a cottage industry
// of blog posts explaining where to find it.
//
// Named for the CATEGORY, not for QuickBooks, deliberately. Label printing
// (printer_registry, since July) and outbound email (a Pages Function with no
// admin surface at all) are both waiting for somewhere to live. Naming this
// screen "QuickBooks" would cost a navigation rewrite the day either moves in.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import { canManageIntegrations } from '../lib/permissions';
import InlineMessage from '../components/InlineMessage';
import useMessage from '../lib/useMessage';
import { matchAllVendors, duplicateSuppliers, MATCH } from '../lib/quickbooksMatch';

const INK = '#0F0F0F';
const MUTED = '#5C5C5C';
const FAINT = '#9A9A9A';
const RULE = '#E0E0E0';
const GOOD = '#1D9E75';
const WARN = '#EF9F27';
const BAD = '#E24B4A';

// #377's shape, reproduced deliberately rather than imported: under `npm start`
// an unknown path returns index.html, so res.json() throws on a page of HTML and
// the real cause reads as the app being broken. Say which it is.
async function callFunction(path, body) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return { error: 'Your session has expired — please sign in again.' };

  let res;
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body || {}),
    });
  } catch (e) {
    return { error: 'Could not reach QuickBooks. ' + (e.message || '') };
  }

  const ct = res.headers.get('Content-Type') || '';
  if (!ct.includes('application/json')) {
    return { error: 'QuickBooks needs the deployed site — this service is not served by the local dev server.' };
  }
  const out = await res.json();
  if (!res.ok) {
    return {
      error: out.error || `Request failed (${res.status})`,
      detail: out.detail || null,
      // Carried through deliberately. "Stede is not connected" and "Stede
      // could not find out" are different answers and only one of them is a
      // reason to go and reconnect anything — see notConnected() below.
      reason: out.reason || null,
      status: res.status,
    };
  }
  return out;
}

// The connection is only reported as ABSENT when QuickBooks-side state actually
// says so: /quickbooks-refdata answers 409 with reason `not_connected` when the
// table holds no live row. Every other failure — the dev server not serving
// /functions, a 502 from Intuit, a dropped network — means the question was not
// answered, and a card that renders those as "Not connected" invites somebody
// to reconnect an integration that was never broken.
function notConnected(state) {
  return state.status === 409 && state.reason === 'not_connected';
}

function relativeAge(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// A category reads better as "Vinyl / Cast" than as "Cast" on its own, and the
// same argument D17 makes for fully qualified ACCOUNT names applies here: two
// categories called "Hardware" under different parents are a coin toss.
function qualifiedCategoryName(cat, byId) {
  const parent = cat.parent_id ? byId.get(cat.parent_id) : null;
  return parent ? `${parent.name} / ${cat.name}` : cat.name;
}

export default function IntegrationsPage() {
  const auth = useAuth();
  const [open, setOpen] = useState(null);

  if (!canManageIntegrations(auth)) {
    return (
      <div style={{ padding: '2rem', maxWidth: 660 }}>
        <InlineMessage type="error" message="Integrations are managed by an administrator or an executive." />
      </div>
    );
  }

  if (open === 'quickbooks') return <QuickBooksScreen onBack={() => setOpen(null)} />;

  return (
    <div style={{ padding: '2rem', maxWidth: 660 }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, margin: '0 0 4px', color: INK }}>Integrations</h1>
        <p style={{ fontSize: 14, color: MUTED, margin: 0 }}>
          Other systems Stede connects to. Each one is set up once and then runs on its own.
        </p>
      </div>
      <IntegrationCard
        name="QuickBooks Online"
        blurb="Send an approved invoice to QuickBooks as a bill, with no file to convert and no third-party importer."
        onOpen={() => setOpen('quickbooks')}
      />
    </div>
  );
}

function IntegrationCard({ name, blurb, onOpen }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The card asks only for the company. Asking for the two lists here would
      // spend a metered read to draw a card nobody has clicked yet — D10.
      const out = await callFunction('/quickbooks-refdata', { want: ['company'] });
      if (!cancelled) setState({ loading: false, ...out });
    })();
    return () => { cancelled = true; };
  }, []);

  const connected = !state.loading && !state.error;

  return (
    <button
      onClick={onOpen}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        background: '#FFF', border: `0.5px solid ${RULE}`, borderRadius: 10,
        padding: '16px 18px', marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: INK }}>{name}</span>
        {state.loading
          ? <StatusPill tone="idle" label="Checking…" />
          : connected
            ? <StatusPill tone="good" label="Connected" />
            : notConnected(state)
              ? <StatusPill tone="bad" label="Not connected" />
              : <StatusPill tone="warn" label="Status unknown" />}
      </div>
      <p style={{ fontSize: 13, color: MUTED, margin: '0 0 8px', lineHeight: 1.45 }}>{blurb}</p>
      {connected && (
        <p style={{ fontSize: 12, color: FAINT, margin: 0 }}>
          {state.companyName || `Company ${state.realmId}`}
          {state.environment === 'sandbox' && ' · Sandbox'}
        </p>
      )}
      {!state.loading && !connected && !notConnected(state) && (
        <p style={{ fontSize: 12, color: WARN, margin: 0 }}>{state.error}</p>
      )}
    </button>
  );
}

function StatusPill({ tone, label }) {
  const colour = tone === 'good' ? GOOD : tone === 'bad' ? BAD : tone === 'warn' ? WARN : FAINT;
  return (
    <span style={{
      fontSize: 10, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase',
      padding: '2px 8px', borderRadius: 20, color: colour, border: `0.5px solid ${colour}`,
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

const TABS = [
  { key: 'connection', label: 'Connection' },
  { key: 'accounts', label: 'GL accounts' },
  { key: 'suppliers', label: 'Suppliers' },
];

function QuickBooksScreen({ onBack }) {
  const msg = useMessage();
  const [tab, setTab] = useState('connection');
  const [refdata, setRefdata] = useState(null);
  // The raw answer, success or failure. Kept beside `refdata` so this screen can
  // tell "QuickBooks says there is no connection" apart from "the question was
  // never answered" — the same distinction notConnected() draws on the card,
  // and it has to be drawn twice because a null refdata is ambiguous on its own.
  const [probe, setProbe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadStede = useCallback(async () => {
    const [cats, vends, cs] = await Promise.all([
      supabase.from('inventory_categories')
        .select('id,name,parent_id,quickbooks_account_id,quickbooks_account_name').order('name'),
      // is_internal is excluded deliberately: a branch that supplies another
      // branch is an internal transfer, not a company QuickBooks holds a
      // supplier record for. Offering it for matching invites somebody to link
      // Houston to whatever name looks closest.
      supabase.from('inventory_vendors')
        .select('id,name,quickbooks_vendor_id,quickbooks_vendor_name')
        .eq('is_active', true).eq('is_internal', false).order('name'),
      supabase.from('company_settings').select('*').single(),
    ]);
    setCategories(cats.data || []);
    setVendors(vends.data || []);
    setSettings(cs.data || null);
  }, []);

  const loadRefdata = useCallback(async (force) => {
    const out = await callFunction('/quickbooks-refdata', {
      want: ['company', 'accounts', 'vendors'],
      refresh: force === true,
    });
    setProbe(out);
    if (out.error) {
      // #437, Session 78n10. Clearing the stale connection is the whole fix,
      // and it is deliberately conditional on WHICH failure this is.
      //
      // Found the first time anybody disconnected: the row was marked ended,
      // the probe correctly reported not-connected, the red banner appeared --
      // and the company name, environment, company id and connected-by all
      // stayed on screen underneath it, because this early return left the
      // last good `refdata` in place. The page said "Stede is not connected"
      // directly above a table describing the connection.
      //
      // NOT CLEARED on any other error, and that is the three-state rule this
      // file already turns on: a probe that could not be reached means the
      // connection is UNKNOWN, not gone. Blanking the details there would
      // claim a disconnection nobody has established, and re-authorising on a
      // false alarm rotates the refresh token for no reason.
      if (notConnected(out)) {
        setRefdata(null);
        // NOT AN ERROR, so it does not get a red banner. "Stede is not
        // connected to QuickBooks" was being rendered as a failure directly
        // above a page whose entire purpose is to say so and offer the button
        // that fixes it -- alarming on the one screen a first-time admin meets
        // before anything has gone wrong. The state is already stated twice in
        // plain text; a third time in red says something is broken.
        setError(null);
      } else {
        setError(out.error);
      }
      return null;
    }
    setError(null);
    setRefdata(out);
    return out;
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadStede(), loadRefdata(false)]);
      setLoading(false);
    })();
  }, [loadStede, loadRefdata]);

  async function handleRefresh() {
    setRefreshing(true);
    const out = await loadRefdata(true);
    setRefreshing(false);
    if (out) msg.showSuccess('Account and supplier lists refreshed from QuickBooks.');
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 660 }}>
      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: MUTED, marginBottom: 14 }}
      >
        ← Integrations
      </button>

      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, margin: '0 0 4px', color: INK }}>QuickBooks Online</h1>
        <p style={{ fontSize: 14, color: MUTED, margin: 0 }}>
          {loading
            ? 'Checking the connection…'
            : refdata?.companyName
              ? <>Connected to <strong style={{ color: INK, fontWeight: 500 }}>{refdata.companyName}</strong>{refdata.environment === 'sandbox' && ' (sandbox)'}.</>
              : probe && notConnected(probe)
                ? 'Not connected to QuickBooks yet.'
                : 'The connection could not be checked.'}
        </p>
      </div>

      {error && <InlineMessage type="error" message={error} style={{ marginBottom: 16 }} />}

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {loading ? (
        <p style={{ fontSize: 14, color: FAINT, padding: '24px 0' }}>Loading…</p>
      ) : tab === 'connection' ? (
        <ConnectionTab
          refdata={refdata}
          probe={probe}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          onChanged={async () => { await Promise.all([loadStede(), loadRefdata(false)]); }}
        />
      ) : tab === 'accounts' ? (
        <AccountsTab refdata={refdata} categories={categories} settings={settings} onSaved={loadStede} msg={msg} />
      ) : (
        <SuppliersTab refdata={refdata} vendors={vendors} onSaved={loadStede} msg={msg} />
      )}
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    // gap 0 and 10px of side padding, measured rather than guessed: at 14px
    // padding with a 4px gap these three labels came to 299px against the 283px
    // a 375px phone actually offers, so "Suppliers" sat just past the edge and
    // needed a nudge-scroll to read. overflowX stays as the backstop for a
    // fourth tab or a larger text size.
    <div style={{ display: 'flex', gap: 0, borderBottom: `0.5px solid ${RULE}`, marginBottom: 20, overflowX: 'auto' }}>
      {tabs.map(t => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 10px', fontSize: 14, whiteSpace: 'nowrap',
            color: active === t.key ? INK : MUTED,
            fontWeight: active === t.key ? 500 : 400,
            borderBottom: active === t.key ? `2px solid ${INK}` : '2px solid transparent',
            marginBottom: -1,
          }}
        >{t.label}</button>
      ))}
    </div>
  );
}

// #436, Session 78n10. Starting and ending the connection.
//
// THIS DID NOT EXIST UNTIL NOW, and the gap is worth recording rather than
// quietly filling. Both Pages Functions shipped at 78n9 and were proven to
// work -- #298 says so, "verified by query rather than by the redirect" --
// because staging was connected by calling the endpoint directly. Nothing in
// `src/` ever called it, so the not-connected state told an admin that "an
// administrator or an executive can connect it" and then offered no way to.
// Found at 78n10 by trying to connect production from the screen, which is the
// first time anybody had.
function ConnectButton({ label, onDone, onError, style }) {
  const [working, setWorking] = useState(false);

  async function go() {
    setWorking(true);
    onError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        onError('Your session has expired — please sign in again.');
        setWorking(false);
        return;
      }
      const res = await fetch('/quickbooks-connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.authorizeUrl) {
        // Surfaced on the page rather than in a console. A missing environment
        // variable comes back named, and reading "Missing environment
        // variables: QBO_TOKEN_KEY" beats reading "something went wrong" by
        // the width of an afternoon.
        onError(body.error || `QuickBooks could not be reached (${res.status}).`);
        setWorking(false);
        return;
      }
      if (onDone) onDone();
      // Leaves Stede entirely. Intuit hosts the sign-in and the consent screen;
      // Stede never sees the password.
      window.location.assign(body.authorizeUrl);
    } catch (e) {
      onError(e && e.message ? e.message : 'The connection could not be started.');
      setWorking(false);
    }
  }

  return (
    <button onClick={go} disabled={working} style={style}>
      {working ? 'Opening QuickBooks…' : label}
    </button>
  );
}

function ConnectionTab({ refdata, probe, onRefresh, refreshing, onChanged }) {
  const [actionError, setActionError] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  async function doDisconnect() {
    setDisconnecting(true);
    setActionError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const res = await fetch('/quickbooks-disconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setActionError(body.error || `Could not disconnect (${res.status}).`);
      } else if (body.revokeError) {
        // Honest rather than tidy: the local disconnect succeeded and Intuit's
        // side did not, which is a real state and the person should know.
        setActionError(
          'Stede has ended the connection, but QuickBooks did not confirm it. '
          + 'Remove Stede under Apps in QuickBooks to be certain. ' + body.revokeError,
        );
      }
      setConfirmDisconnect(false);
      if (onChanged) await onChanged();
    } catch (e) {
      setActionError(e && e.message ? e.message : 'Could not disconnect.');
    } finally {
      setDisconnecting(false);
    }
  }

  if (!refdata) {
    // Same three-state rule as the card. Telling somebody they are not
    // connected when the truth is that nothing could be reached sends them to
    // re-authorise, and re-authorising is the one action on this screen with a
    // real consequence: it rotates the refresh token.
    //
    // THE BUTTON HANGS OFF THAT RULE and is deliberately not shown in the
    // unknown state, for the reason the comment above already gives.
    return probe && notConnected(probe)
      ? (
        <div>
          <p style={{ fontSize: 14, color: MUTED, margin: '0 0 14px' }}>
            Stede is not connected to QuickBooks yet.
          </p>
          {actionError && <InlineMessage type="error" message={actionError} style={{ marginBottom: 12 }} />}
          <ConnectButton
            label="Connect to QuickBooks"
            onError={setActionError}
            style={{
              padding: '10px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: INK, color: '#fff', fontSize: 14, fontWeight: 500,
            }}
          />
          <p style={{ fontSize: 12.5, color: FAINT, margin: '10px 0 0', lineHeight: 1.5 }}>
            You will be taken to Intuit to sign in and approve the connection.
            Stede never sees your QuickBooks password.
          </p>
        </div>
      )
      : (
        <div>
          <p style={{ fontSize: 14, color: MUTED, margin: '0 0 8px' }}>
            The connection could not be checked, so whether Stede is connected is unknown.
          </p>
          {probe?.error && <p style={{ fontSize: 13, color: WARN, margin: 0 }}>{probe.error}</p>}
        </div>
      );
  }
  const rows = [
    ['Company', refdata.companyName || '—'],
    ['Environment', refdata.environment === 'sandbox' ? 'Sandbox (test company)' : 'Production'],
    ['Company ID', refdata.realmId],
    ['Connected by', refdata.connectedBy || '—'],
    ['Connected', refdata.connectedAt ? new Date(refdata.connectedAt).toLocaleString() : '—'],
  ];
  return (
    <div>
      {refdata.environment === 'sandbox' && (
        <InlineMessage
          type="warning"
          message="This is Intuit's test company, not D&R's books. Bills sent from here are not real."
          style={{ marginBottom: 16 }}
        />
      )}
      {rows.map(([k, v], i) => (
        <div key={k} style={{
          display: 'flex', justifyContent: 'space-between', gap: 16,
          padding: '11px 0', borderBottom: i === rows.length - 1 ? 'none' : `0.5px solid ${RULE}`,
        }}>
          <span style={{ fontSize: 13, color: MUTED }}>{k}</span>
          <span style={{ fontSize: 13, color: INK, textAlign: 'right', wordBreak: 'break-all' }}>{v}</span>
        </div>
      ))}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: `0.5px solid ${RULE}` }}>
        <p style={{ fontSize: 12, color: FAINT, margin: '0 0 10px' }}>
          Account and supplier lists were last read from QuickBooks {relativeAge(refdata.accountsCachedAt) || 'just now'}.
          They refresh themselves daily.
        </p>
        <button className="btn btn-secondary btn-sm" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>

      {/* #436. Ending the connection, and it is deliberately a two-step
          action: the first press asks, the second does it. Disconnecting is
          not destructive -- bills already sent stay where they are -- but it
          does stop the accounts-payable path working until somebody
          reconnects, and that is not a thing to do by mis-click. */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: `0.5px solid ${RULE}` }}>
        {actionError && <InlineMessage type="error" message={actionError} style={{ marginBottom: 12 }} />}
        {confirmDisconnect ? (
          <div>
            <p style={{ fontSize: 13, color: INK, margin: '0 0 4px' }}>
              Disconnect Stede from {refdata.companyName || 'this QuickBooks company'}?
            </p>
            <p style={{ fontSize: 12.5, color: MUTED, margin: '0 0 12px', lineHeight: 1.5 }}>
              Bills already sent stay in QuickBooks. Stede stops being able to send new ones
              until it is reconnected.
            </p>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setConfirmDisconnect(false)}
              disabled={disconnecting}
              style={{ marginRight: 8 }}
            >
              Cancel
            </button>
            <button
              onClick={doDisconnect}
              disabled={disconnecting}
              style={{
                padding: '7px 14px', borderRadius: 7, border: '1px solid #A32D2D',
                background: disconnecting ? '#E0E0E0' : '#A32D2D', color: '#fff',
                fontSize: 13, fontWeight: 600, cursor: disconnecting ? 'not-allowed' : 'pointer',
              }}
            >
              {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => { setActionError(null); setConfirmDisconnect(true); }}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 13, color: '#A32D2D', textDecoration: 'underline',
            }}
          >
            Disconnect from QuickBooks
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GL accounts
// ---------------------------------------------------------------------------

// D17. Accounts are shown by their FULLY QUALIFIED name, and this is not
// hypothetical — verified against the connected sandbox at Session 78n9.3.
//
// The gate cited two accounts named "Job Materials" under different parents.
// That example does not exist in the connected company; there is exactly one
// Job Materials. The hazard is real with a DIFFERENT account: `Equipment
// Rental` appears twice, once at the top level and once as `Job
// Expenses:Equipment Rental`, both Expense type. A bare-name picker offers two
// identical rows and QuickBooks accepts whichever is chosen. The gate's
// conclusion stands; its stated evidence was wrong and is corrected here.
function AccountSelect({ value, accounts, onChange, disabled, placeholder }) {
  const groups = useMemo(() => {
    const g = new Map();
    (accounts || []).forEach(a => {
      if (!g.has(a.type)) g.set(a.type, []);
      g.get(a.type).push(a);
    });
    return [...g.entries()];
  }, [accounts]);

  return (
    <select
      value={value || ''}
      disabled={disabled}
      onChange={e => onChange(e.target.value || null)}
      // 16px is the global floor — anything smaller triggers iOS auto-zoom.
      style={{
        fontSize: 16, padding: '8px 10px', width: '100%',
        border: `0.5px solid ${RULE}`, borderRadius: 6, background: '#FFF', color: INK,
      }}
    >
      <option value="">{placeholder || 'Not mapped'}</option>
      {groups.map(([type, list]) => (
        <optgroup key={type} label={type}>
          {list.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function AccountsTab({ refdata, categories, settings, onSaved, msg }) {
  const [saving, setSaving] = useState(null);
  const accounts = refdata?.accounts || [];
  const accountsById = useMemo(() => new Map(accounts.map(a => [a.id, a])), [accounts]);
  const catsById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

  const defaultId = settings?.quickbooks_default_account_id || null;

  async function saveDefault(accountId) {
    const acct = accountId ? accountsById.get(accountId) : null;
    setSaving('default');
    const { error } = await supabase
      .from('company_settings')
      .update({
        quickbooks_default_account_id: accountId,
        // D20: the id is the identity, the name is for reading.
        quickbooks_default_account_name: acct ? acct.name : null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', settings.id);
    setSaving(null);
    if (error) { msg.showError('Could not save the default account. ' + error.message); return; }
    await onSaved();
  }

  async function saveCategory(cat, accountId) {
    const acct = accountId ? accountsById.get(accountId) : null;
    setSaving(cat.id);
    const { error } = await supabase
      .from('inventory_categories')
      .update({
        quickbooks_account_id: accountId,
        quickbooks_account_name: acct ? acct.name : null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', cat.id);
    setSaving(null);
    if (error) { msg.showError('Could not save that mapping. ' + error.message); return; }
    await onSaved();
  }

  // D15: unmapped float to the top with a count, because the list's job is to
  // show what still needs doing rather than to be an alphabetical reference.
  const { unmapped, mapped } = useMemo(() => {
    const u = [], m = [];
    categories.forEach(c => (c.quickbooks_account_id ? m : u).push(c));
    return { unmapped: u, mapped: m };
  }, [categories]);

  return (
    <div>
      {msg.message && <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} style={{ marginBottom: 16 }} />}

      {/* D16. Stated at the top and it stays until a default is set, because
          an off-catalog line has no category by definition — item_id is NULL
          by design — so without a default the first real send fails on a
          blocker nobody could have anticipated from this screen. */}
      {!defaultId && (
        <InlineMessage
          type="warning"
          message="Set a default account before sending any bill. Off-catalog lines have no category, so this is the account they book to."
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: INK, margin: '0 0 2px' }}>Default account</p>
        <p style={{ fontSize: 12, color: MUTED, margin: '0 0 8px', lineHeight: 1.45 }}>
          Used for anything with no category of its own — every off-catalog line.
        </p>
        <AccountSelect
          value={defaultId}
          accounts={accounts}
          disabled={saving === 'default'}
          onChange={saveDefault}
          placeholder="Choose an account…"
        />
        <MappingFlag id={defaultId} storedName={settings?.quickbooks_default_account_name} accountsById={accountsById} />
      </div>

      <p style={{
        fontSize: 11, fontWeight: 500, color: FAINT, textTransform: 'uppercase', letterSpacing: '0.06em',
        margin: '0 0 10px', paddingBottom: 8, borderBottom: `0.5px solid ${RULE}`,
      }}>
        Categories
        {unmapped.length > 0 && ` · ${unmapped.length} still to map`}
      </p>

      {categories.length === 0 && (
        <p style={{ fontSize: 14, color: FAINT, padding: '16px 0' }}>No categories yet.</p>
      )}

      {[...unmapped, ...mapped].map(cat => (
        <div key={cat.id} style={{ padding: '12px 0', borderBottom: `0.5px solid ${RULE}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 14, color: INK }}>{qualifiedCategoryName(cat, catsById)}</span>
            {!cat.quickbooks_account_id && <StatusPill tone="warn" label="Unmapped" />}
          </div>
          <AccountSelect
            value={cat.quickbooks_account_id}
            accounts={accounts}
            disabled={saving === cat.id}
            onChange={id => saveCategory(cat, id)}
          />
          <MappingFlag id={cat.quickbooks_account_id} storedName={cat.quickbooks_account_name} accountsById={accountsById} />
        </div>
      ))}
    </div>
  );
}

// D20 and D21, the two failures that otherwise surface during a month-end close
// as an Intuit fault code.
//
// D21 — the account is GONE from QuickBooks. The refresh already holds the full
// list, so checking costs nothing and catches it before a bill hits it.
//
// D20 — the account was RENAMED. Deliberately flagged rather than silently
// corrected: a renamed account is usually fine, and an account renamed because
// it was repurposed is a mapping that now points somewhere nobody intended. A
// silent auto-correct hides exactly the case that matters.
function MappingFlag({ id, storedName, accountsById }) {
  if (!id) return null;
  const live = accountsById.get(id);

  if (!live) {
    return (
      <p style={{ fontSize: 12, color: BAD, margin: '6px 0 0' }}>
        This account no longer exists in QuickBooks{storedName ? ` (was “${storedName}”)` : ''}. Choose another before sending a bill.
      </p>
    );
  }
  if (storedName && storedName !== live.name) {
    return (
      <p style={{ fontSize: 12, color: WARN, margin: '6px 0 0' }}>
        Renamed in QuickBooks — was “{storedName}”, now “{live.name}”. Still linked; re-pick if that is not the account you meant.
      </p>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Suppliers — D22
// ---------------------------------------------------------------------------

const CONFIDENCE_LABEL = {
  [MATCH.EXACT]: 'Exact match',
  [MATCH.LIKELY]: 'Likely match',
  [MATCH.POSSIBLE]: 'Possible match',
  [MATCH.NONE]: 'No match found',
};

function SupplierSelect({ value, qbVendors, onChange, disabled }) {
  return (
    <select
      value={value || ''}
      disabled={disabled}
      onChange={e => onChange(e.target.value || null)}
      style={{
        fontSize: 16, padding: '8px 10px', width: '100%',
        border: `0.5px solid ${RULE}`, borderRadius: 6, background: '#FFF', color: INK,
      }}
    >
      <option value="">Not linked</option>
      {(qbVendors || []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
    </select>
  );
}

function SuppliersTab({ refdata, vendors, onSaved, msg }) {
  const [saving, setSaving] = useState(null);
  const [linkingAll, setLinkingAll] = useState(false);
  const qbVendors = refdata?.vendors || [];
  const qbById = useMemo(() => new Map(qbVendors.map(v => [v.id, v])), [qbVendors]);

  const buckets = useMemo(() => matchAllVendors(vendors, qbVendors), [vendors, qbVendors]);
  const dupes = useMemo(() => duplicateSuppliers(qbVendors), [qbVendors]);

  async function link(vendor, qbId) {
    const qb = qbId ? qbById.get(qbId) : null;
    setSaving(vendor.id);
    const { error } = await supabase
      .from('inventory_vendors')
      .update({
        // The id is the link and survives a rename on either side. The name is
        // stored alongside it purely so this screen can say what it points at
        // — #298 is explicit that the name must never be used as identity.
        quickbooks_vendor_id: qbId,
        quickbooks_vendor_name: qb ? qb.name : null,
      })
      .eq('id', vendor.id);
    setSaving(null);
    if (error) { msg.showError('Could not link that supplier. ' + error.message); return false; }
    await onSaved();
    return true;
  }

  // D22's high bar, applied in bulk. `auto` only ever holds vendors where
  // exactly one QuickBooks supplier matched exactly after normalisation —
  // suggestVendorMatch returns autoLinkable false the moment two do, because
  // picking the first would be a coin toss dressed up as an answer.
  async function linkAllAuto() {
    setLinkingAll(true);
    let done = 0;
    for (const row of buckets.auto) {
      const { error } = await supabase
        .from('inventory_vendors')
        .update({ quickbooks_vendor_id: row.suggestion.best.id, quickbooks_vendor_name: row.suggestion.best.name })
        .eq('id', row.vendor.id);
      if (!error) done += 1;
    }
    setLinkingAll(false);
    await onSaved();
    msg.showSuccess(`${done} supplier${done === 1 ? '' : 's'} linked.`);
  }

  const needsAttention = [...buckets.auto, ...buckets.review, ...buckets.unmatched];

  return (
    <div>
      {msg.message && <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} style={{ marginBottom: 16 }} />}

      {dupes.length > 0 && (
        <InlineMessage
          type="warning"
          message={`QuickBooks has ${dupes.length} duplicate supplier name${dupes.length === 1 ? '' : 's'}: ${dupes.map(d => d.vendors[0].name).join(', ')}. Stede will not link these by itself — pick the right one.`}
          style={{ marginBottom: 16 }}
        />
      )}

      <p style={{ fontSize: 13, color: MUTED, margin: '0 0 14px', lineHeight: 1.5 }}>
        Linking is a one-time job per vendor. Once linked, either system can rename freely and the link survives,
        because it is stored as QuickBooks&rsquo; own supplier ID rather than a name.
      </p>

      {/* #437, Colby's request at the 78n10 rehearsal: say how many of how
          many, rather than only announcing completion.

          THE ZERO CASE IS THE REASON THIS EXISTS. On production after the
          #294 wipe there are no external vendors at all, and the screen said
          "Every vendor is linked to a QuickBooks supplier" -- vacuously true,
          zero of zero, and it reads as "your setup is done" when the honest
          reading is "there is nothing here yet". Same class as #255, where a
          "queue is clear" empty state had never been rendered against a
          genuinely empty queue: the done-message and the empty-message look
          identical, and only one of them is worth acting on.

          Internal vendors are already excluded upstream by the query, so the
          denominator is external, active vendors -- the ones that can have a
          QuickBooks supplier at all. */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
        padding: '10px 12px', marginBottom: 18, borderRadius: 8,
        border: `0.5px solid ${RULE}`, background: '#FAFAFA',
      }}>
        {vendors.length === 0 ? (
          <>
            <span style={{ fontSize: 13.5, color: INK, fontWeight: 500 }}>No vendors to link yet</span>
            <span style={{ fontSize: 12.5, color: MUTED }}>
              — vendors added in Stede appear here to be matched to a QuickBooks supplier.
            </span>
          </>
        ) : (
          <>
            <span style={{
              fontSize: 13.5, fontWeight: 500,
              color: buckets.linked.length === vendors.length ? GOOD : INK,
            }}>
              {buckets.linked.length} of {vendors.length} vendor{vendors.length === 1 ? '' : 's'} linked
            </span>
            {needsAttention.length > 0 && (
              <span style={{ fontSize: 12.5, color: MUTED }}>
                — {needsAttention.length} still to match.
              </span>
            )}
          </>
        )}
      </div>

      {buckets.auto.length > 0 && (
        <div style={{
          border: `0.5px solid ${RULE}`, borderRadius: 8, padding: 14, marginBottom: 20, background: '#FAFAFA',
        }}>
          <p style={{ fontSize: 13, fontWeight: 500, color: INK, margin: '0 0 4px' }}>
            {buckets.auto.length} exact match{buckets.auto.length === 1 ? '' : 'es'} found
          </p>
          <p style={{ fontSize: 12, color: MUTED, margin: '0 0 10px', lineHeight: 1.45 }}>
            Each of these matched exactly one QuickBooks supplier by name. Anything less certain is left for you below.
          </p>
          <button className="btn btn-primary btn-sm" onClick={linkAllAuto} disabled={linkingAll}>
            {linkingAll ? 'Linking…' : `Link all ${buckets.auto.length}`}
          </button>
        </div>
      )}

      {/* The header is suppressed when the bucket is empty: rendering "NOT
          LINKED" directly above "Every vendor is linked" reads as a
          contradiction, which is how it looked in the live test. */}
      {needsAttention.length > 0 ? (
        <p style={{
          fontSize: 11, fontWeight: 500, color: FAINT, textTransform: 'uppercase', letterSpacing: '0.06em',
          margin: '0 0 10px', paddingBottom: 8, borderBottom: `0.5px solid ${RULE}`,
        }}>
          Not linked · {needsAttention.length}
        </p>
      ) : vendors.length > 0 ? (
        <p style={{ fontSize: 14, color: GOOD, padding: '4px 0 12px', margin: 0 }}>
          Every vendor is linked to a QuickBooks supplier.
        </p>
      ) : null /* nothing to celebrate when there is nothing to link — the
                  count line above has already said so honestly */}

      {needsAttention.map(row => (
        <div key={row.vendor.id} style={{ padding: '12px 0', borderBottom: `0.5px solid ${RULE}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: INK }}>{row.vendor.name}</span>
            <StatusPill
              tone={row.suggestion.confidence === MATCH.NONE ? 'idle' : row.suggestion.autoLinkable ? 'good' : 'warn'}
              label={CONFIDENCE_LABEL[row.suggestion.confidence] || 'Unmatched'}
            />
          </div>
          {row.suggestion.ambiguous && (
            <p style={{ fontSize: 12, color: WARN, margin: '0 0 6px' }}>
              More than one QuickBooks supplier has this exact name. Stede will not guess.
            </p>
          )}
          <SupplierSelect
            value={row.vendor.quickbooks_vendor_id}
            qbVendors={row.suggestion.candidates.length > 0 ? row.suggestion.candidates : qbVendors}
            disabled={saving === row.vendor.id}
            onChange={id => link(row.vendor, id)}
          />
          {row.suggestion.candidates.length > 0 && row.suggestion.candidates.length < qbVendors.length && (
            <p style={{ fontSize: 12, color: FAINT, margin: '6px 0 0' }}>
              Showing {row.suggestion.candidates.length} close name{row.suggestion.candidates.length === 1 ? '' : 's'}.
            </p>
          )}
        </div>
      ))}

      {buckets.linked.length > 0 && (
        <>
          <p style={{
            fontSize: 11, fontWeight: 500, color: FAINT, textTransform: 'uppercase', letterSpacing: '0.06em',
            margin: '26px 0 10px', paddingBottom: 8, borderBottom: `0.5px solid ${RULE}`,
          }}>
            Linked · {buckets.linked.length}
          </p>
          {buckets.linked.map(({ vendor }) => (
            <LinkedSupplierRow
              key={vendor.id}
              vendor={vendor}
              qbById={qbById}
              qbVendors={qbVendors}
              disabled={saving === vendor.id}
              onChange={id => link(vendor, id)}
            />
          ))}
        </>
      )}
    </div>
  );
}

// The supplier equivalent of MappingFlag, and it exists for the same reason:
// a link pointing at a supplier that has been deleted or deactivated in
// QuickBooks fails at send time, during a close, when nobody wants to be
// diagnosing it. The list is already in hand.
function LinkedSupplierRow({ vendor, qbById, qbVendors, disabled, onChange }) {
  const live = qbById.get(vendor.quickbooks_vendor_id);
  const renamed = live && vendor.quickbooks_vendor_name && vendor.quickbooks_vendor_name !== live.name;

  return (
    <div style={{ padding: '12px 0', borderBottom: `0.5px solid ${RULE}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, color: INK }}>{vendor.name}</span>
        {!live && <StatusPill tone="bad" label="Missing in QuickBooks" />}
        {renamed && <StatusPill tone="warn" label="Renamed" />}
      </div>
      {live && !renamed && (
        <p style={{ fontSize: 13, color: MUTED, margin: '0 0 6px' }}>→ {live.name}</p>
      )}
      {renamed && (
        <p style={{ fontSize: 12, color: WARN, margin: '0 0 6px' }}>
          Was “{vendor.quickbooks_vendor_name}”, now “{live.name}”. Still linked by ID, so nothing is broken.
        </p>
      )}
      {!live && (
        <p style={{ fontSize: 12, color: BAD, margin: '0 0 6px' }}>
          This supplier is no longer in QuickBooks{vendor.quickbooks_vendor_name ? ` (was “${vendor.quickbooks_vendor_name}”)` : ''}. Bills to this vendor will fail until it is re-linked.
        </p>
      )}
      <SupplierSelect value={vendor.quickbooks_vendor_id} qbVendors={qbVendors} disabled={disabled} onChange={onChange} />
    </div>
  );
}
